import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const MoneyBackGuarantee: React.FC = () => {
  useEffect(() => {
    document.title = 'BuyDrop Money Back Guarantee — Buy Drop';
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-3xl">
        <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="text-green-700 hover:text-green-800 font-medium">
            Home
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-700">Money Back Guarantee</span>
        </nav>

        <article className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-10">
          <p className="text-sm font-medium text-amber-800 mb-2">Shop with confidence</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            BuyDrop Money Back Guarantee
          </h1>
          <p className="text-base text-gray-700 mt-4 leading-relaxed">
            Get the item you ordered or your money back. After delivery, your payment stays in escrow
            for 48 hours before it is released to the seller.
          </p>

          <div className="mt-8 space-y-6 text-sm text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Payment protection</h2>
              <p>
                When you pay, your funds go into escrow — not paid out to the seller right away. After
                delivery, we hold them for 48 hours so you have time to confirm the order went as
                expected. If you report a problem during that 48-hour window, we can stop the payout and
                help resolve the issue.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">What it covers</h2>
              <p>
                When a listing shows the BuyDrop Money Back Guarantee, you can shop knowing that if
                the item is not as described or you do not receive it, you can request a full refund
                within 48 hours of delivery while your payment is in escrow. If a listing or order
                shows a different reporting window set by the seller, follow that deadline instead.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">How it works</h2>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Buy from a listing that offers the BuyDrop Money Back Guarantee.</li>
                <li>Your payment stays in escrow for 48 hours after delivery.</li>
                <li>If something goes wrong, report the issue within 48 hours of delivery.</li>
                <li>We review the request and issue a refund when the guarantee applies.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">How to dispute an issue</h2>
              <p className="mb-3">
                If your order was not delivered, arrived damaged, or does not match the listing,
                start a dispute from your order:
              </p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>
                  Open{' '}
                  <Link to="/my-orders" className="text-green-700 font-medium hover:underline">
                    My Orders
                  </Link>{' '}
                  and select the order.
                </li>
                <li>
                  Choose <span className="font-medium text-gray-900">Report a problem</span> (or
                  contact support from the order).
                </li>
                <li>
                  Pick the reason (for example: not received, not as described, damaged) and add a
                  short description.
                </li>
                <li>
                  Attach photos when the issue is about condition or description — clear photos help
                  us resolve faster.
                </li>
                <li>Submit the report within 48 hours of delivery (or the window shown on the listing or order, if different).</li>
              </ol>
              <p className="mt-3">
                We review disputes in order and may contact you or the seller for more details.
                Eligible orders under this guarantee are refunded in full.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">What you need</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Proof of purchase through Buy Drop</li>
                <li>A clear reason (item not received, not as described, damaged, or similar)</li>
                <li>Photos when the issue is about the item&apos;s condition or description</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Questions</h2>
              <p>
                Email{' '}
                <a
                  href="mailto:support@buydrop.com"
                  className="text-green-700 font-medium hover:underline"
                >
                  support@buydrop.com
                </a>{' '}
                or visit the{' '}
                <Link to="/help" className="text-green-700 font-medium hover:underline">
                  Help Center
                </Link>
                .
              </p>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
};

export default MoneyBackGuarantee;
